import Moment from 'moment'
import { END } from '@/state/types/fetch'
import {
  GET_LIST,
  GET_ONE,
  GET_MANY,
  GET_SIMPLE,
  GET_MANY_REFERENCE,
  CREATE,
  UPDATE,
} from '@/state/types/data'
import {
  EDITING_STARTED,
  EDITING_ENDED,
  EDIT_COMMITTED,
  USER_DISCONNECTED,
} from '@/state/types/editing'

/**
 * The data state is an instance pool, which keeps track of the fetch date of each instance.
 *
 * @example
 * {
 *   23: { id: 23, title: 'War and Peace' },
 *   67: { id: 67, title: 'Anna Karenina' },
 *   fetchedAt: { // non enumerable
 *     23: new Date('2016-08-05T19:33:15.012Z'),
 *     67: new Date('2016-08-05T19:33:43.449Z'),
 *   },
 * }
 */

const cacheDuration = 10 * 60 * 1000 // ten minutes

/**
 * Add new records to the pool, and remove outdated ones.
 *
 * This is the equivalent of a stale-while-revalidate caching strategy:
 * The cached data is displayed before fetching, and stale data is removed
 * only once fresh data is fetched.
 */
const addRecords = (newRecords = [], oldRecords) => {
  // prepare new records and timestamp them
  const newRecordsById = newRecords.reduce((prev, record) => {
    prev[record.id] = record // eslint-disable-line no-param-reassign
    return prev
  }, {})
  const now = new Date()
  const newRecordsFetchedAt = newRecords.reduce((prev, record) => {
    prev[record.id] = now // eslint-disable-line no-param-reassign
    return prev
  }, {})
  // remove outdated old records
  const latestValidDate = new Date()
  latestValidDate.setTime(latestValidDate.getTime() - cacheDuration)
  const oldValidRecordIds = oldRecords.fetchedAt
    ? Object.keys(oldRecords.fetchedAt).filter(id => oldRecords.fetchedAt[id] > latestValidDate)
    : []
  const oldValidRecords = oldValidRecordIds.reduce((prev, id) => {
    prev[id] = oldRecords[id] // eslint-disable-line no-param-reassign
    return prev
  }, {})
  const oldValidRecordsFetchedAt = oldValidRecordIds.reduce((prev, id) => {
    prev[id] = oldRecords.fetchedAt[id] // eslint-disable-line no-param-reassign
    return prev
  }, {})
  // combine old records and new records
  const records = {
    ...oldValidRecords,
    ...newRecordsById,
  }
  Object.defineProperty(records, 'fetchedAt', {
    value: {
      ...oldValidRecordsFetchedAt,
      ...newRecordsFetchedAt,
    },
  }) // non enumerable by default
  return records
}

const initialState = {}
Object.defineProperty(initialState, 'fetchedAt', { value: {} }) // non enumerable by default

const updateRecord = (payload, oldRecords) => {
  const now = new Date()
  const newRecordsById = {
    [payload.nlData.id]: {
      // Make sure that old data is preserved, put old record first and
      // then overwrite it with new one in case new record has only updated properties
      ...oldRecords[payload.nlData.id],
      ...payload.nlData,
    },
  }
  const newRecordsFetchedAt = { [payload.nlData.id]: now }
  const oldRecordsIds = oldRecords.fetchedAt ? Object.keys(oldRecords.fetchedAt) : []
  const oldRecordsFetchedAt = oldRecordsIds.reduce((prev, id) => {
    prev[id] = oldRecords.fetchedAt[id]
    return prev
  }, {})
  // combine old records and new records
  const records = {
    ...oldRecords,
    ...newRecordsById,
  }
  Object.defineProperty(records, 'fetchedAt', {
    value: {
      ...oldRecordsFetchedAt,
      ...newRecordsFetchedAt,
    },
  })
  return records
}

const updateRecords = (payload, oldRecords) => {
  const oldRecordsIds = oldRecords.fetchedAt ? Object.keys(oldRecords.fetchedAt) : []
  const oldRecordsFetchedAt = oldRecordsIds.reduce((prev, id) => {
    prev[id] = oldRecords.fetchedAt[id]
    return prev
  }, {})
  const updatedRecordsIds = oldRecordsIds.filter(
    rec => oldRecords[rec].edited_by && oldRecords[rec].edited_by.id === payload.userId,
  )
  const updatedRecords = updatedRecordsIds.reduce((prev, id) => {
    prev[id] = { ...oldRecords[id], edited_lock: false }
    return prev
  }, {})

  const records = {
    ...oldRecords,
    ...updatedRecords,
  }
  Object.defineProperty(records, 'fetchedAt', {
    value: {
      ...oldRecordsFetchedAt,
    },
  })
  return records
}

export default (resource, options) => (previousState = initialState, { payload, type, meta }) => {
  if (!meta || meta.resource !== resource) {
    return previousState
  }

  // Handle additional messages
  if (type === EDIT_COMMITTED) {
    return updateRecord(payload, previousState)
  } else if (type === USER_DISCONNECTED) {
    return updateRecords(payload, previousState)
  } else if (type === EDITING_STARTED) {
    const data = {
      nlData: {
        id: payload.newsletterId,
        edited_by: payload.user,
        edited_lock: true,
        edited_at: Moment().toISOString(),
      },
    }
    return updateRecord(data, previousState)
  } else if (type === EDITING_ENDED) {
    const data = {
      nlData: {
        id: payload,
        edited_lock: false,
        edited_at: Moment().toISOString(),
      },
    }
    return updateRecord(data, previousState)
  }

  // Handle ajax fetch responses
  if (!meta.fetchResponse || meta.fetchStatus !== END) {
    return previousState
  }
  switch (meta.fetchResponse) {
    case GET_LIST:
    case GET_MANY:
    case GET_MANY_REFERENCE:
      return addRecords(payload.data, previousState)
    case GET_ONE:
    case UPDATE:
    case CREATE:
      return addRecords([payload.data], previousState)
    case GET_SIMPLE:
      return payload.data
    default:
      return previousState
  }
}

export const getRecord = (state, id) => state[id]



// WEBPACK FOOTER //
// ./state/reducers/resource/data.js